import { FC } from 'react';
import styled from 'styled-components';
import { Binoculars24 } from 'ui/icons';
import { colors, spacing } from 'ui/theme';
import {
  FrequencyManagementMessageType,
  FrequencyMode,
  SupportedMediums,
} from 'modules/frequencyManagement/lib/types';
import { sortMessageTypes } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import { MessageTypesBreakdown } from './MessageTypesBreakdown';
import { ConfigByMedium } from 'modules/frequencyManagement/lib/types';
import { ManageSettingsLink } from 'modules/frequencyManagement/lib/components/summaryBanner/ManageSettingsLink';

export const Wrapper = styled.div`
  background: ${colors.backgroundTertiary};
  border-radius: ${spacing.half};
  border: ${spacing.single} solid ${colors.borderPrimarySubtle};
  padding: ${spacing.plusHalf};

  &[data-external='true'] {
    display: flex;
    justify-content: space-between;
    background: ${colors.backgroundSecondary};
    border: 0;
  }
`;

export const Binoculars24Margin = styled(Binoculars24)`
  margin-right: ${spacing.quarter};
`;

export const FlexHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
  justify-content: flex-start;
  flex-wrap: wrap;
`;

export const getMessageTypesSummary = (
  messageTypesForMedium: Array<FrequencyManagementMessageType>,
) => {
  /** Ensure message types names are sorted in list */
  const messageTypes = sortMessageTypes(messageTypesForMedium);
  return messageTypes.reduce(
    (acc, messageType) => {
      if (
        (messageType.frequencyManagement.settings === null ||
          messageType.frequencyManagement.settings?.mode === FrequencyMode.Off) &&
        messageType.frequencyManagement.ignoreCap === false
      ) {
        return {
          ...acc,
          offNames: acc.offNames.concat(messageType.name),
        };
      }
      return {
        ...acc,
        onNames: acc.onNames.concat(messageType.name),
      };
    },
    {
      offNames: [] as Array<string>,
      onNames: [] as Array<string>,
    },
  );
};

export interface SummaryBannerProps {
  messageMedium: SupportedMediums;
  config: ConfigByMedium;
  asExternalContent?: boolean;
}
export const SummaryBanner: FC<SummaryBannerProps> = ({
  asExternalContent = false,
  config,
  messageMedium,
}) => {
  const messageTypes = config[messageMedium].messageTypes || [];
  const { offNames, onNames } = getMessageTypesSummary(messageTypes);
  const textColor = asExternalContent ? colors.iconPrimary : colors.iconInteractive;
  return (
    <Wrapper data-external={asExternalContent}>
      <FlexHeading>
        <Binoculars24Margin color={textColor} />
        <MessageTypesBreakdown
          asExternalContent={asExternalContent}
          isForOnType={false}
          messageMedium={messageMedium}
          names={offNames}
        />
        <MessageTypesBreakdown
          asExternalContent={asExternalContent}
          isForOnType={true}
          messageMedium={messageMedium}
          names={onNames}
        />
      </FlexHeading>
      {asExternalContent && <ManageSettingsLink messageMedium={messageMedium} />}
    </Wrapper>
  );
};
